/**
 * Gateway backend service stubs.
 * Each function wraps a backend RPC or Edge Function call.
 * Business logic (sequence_id allocation, lifecycle side effects, etc.) lives server-side.
 */

import { supabase } from '@/integrations/supabase/client';
import { invokeAuthedFunction } from '@/lib/invokeAuthedFunction';
import type { GatewayCommand, GatewayDevice, CommissioningCheckResult } from '@/types/gateway';

// ---------------------------------------------------------------------------
// Device linking
// ---------------------------------------------------------------------------

export interface LinkGatewayResult {
  device_id: string;
  status: 'created' | 'linked' | 'already_linked';
}

/** Link a gateway to a property via claim code. Backend validates and handles conflicts. */
export async function linkGatewayToProperty(
  claimCode: string,
  propertyId: string | null,
  profileId?: string | null
): Promise<LinkGatewayResult> {
  const body: { claim_code: string; property_id?: string; profile_id?: string } = {
    claim_code: claimCode,
  };
  if (propertyId) body.property_id = propertyId;
  if (profileId) body.profile_id = profileId;
  return invokeAuthedFunction<LinkGatewayResult>(supabase, 'gateway-link-device', body);
}

// ---------------------------------------------------------------------------
// Commands
// ---------------------------------------------------------------------------

/** Issue a gateway command. Backend atomically allocates sequence_id. */
export async function issueGatewayCommand(
  deviceId: string,
  commandType: string,
  payload: Record<string, unknown> = {}
): Promise<GatewayCommand> {
  return invokeAuthedFunction<GatewayCommand>(supabase, 'gateway-issue-command', {
    device_id: deviceId,
    command_type: commandType,
    payload,
  });
}

// ---------------------------------------------------------------------------
// Commissioning
// ---------------------------------------------------------------------------

/** Fetch server-computed commissioning checks. */
export async function computeGatewayCommissioningChecks(
  deviceId: string
): Promise<CommissioningCheckResult[]> {
  return invokeAuthedFunction<CommissioningCheckResult[]>(supabase, 'gateway-commissioning-checks', {
    device_id: deviceId,
  });
}

export interface CompleteCommissioningInput {
  deviceId: string;
  overrides: { check_name: string; note: string }[];
  busDevicesExpected: number;
}

/** Complete commissioning — backend handles all side effects. */
export async function completeGatewayCommissioning(
  input: CompleteCommissioningInput
): Promise<{ success: boolean }> {
  return invokeAuthedFunction<{ success: boolean }>(supabase, 'gateway-complete-commissioning', {
    device_id: input.deviceId,
    overrides: input.overrides,
    bus_devices_expected: input.busDevicesExpected,
  });
}

// ---------------------------------------------------------------------------
// Lifecycle management
// ---------------------------------------------------------------------------

/** Replace a gateway device. Backend decommissions old, links new, transfers config. */
export async function replaceGateway(
  oldDeviceId: string,
  newClaimCode: string
): Promise<{ new_device_id: string }> {
  return invokeAuthedFunction<{ new_device_id: string }>(supabase, 'gateway-replace-device', {
    old_device_id: oldDeviceId,
    new_claim_code: newClaimCode,
  });
}

/** Reassign a gateway to a different property. Backend handles side effects. */
export async function reassignGateway(
  deviceId: string,
  newPropertyId: string
): Promise<{ success: boolean }> {
  return invokeAuthedFunction<{ success: boolean }>(supabase, 'gateway-reassign-device', {
    device_id: deviceId,
    new_property_id: newPropertyId,
  });
}

/** Update gateway lifecycle status. Backend validates transition and handles side effects. */
export async function setGatewayLifecycleStatus(
  deviceId: string,
  nextStatus: string
): Promise<{ lifecycle_status: string }> {
  return invokeAuthedFunction<{ lifecycle_status: string }>(supabase, 'gateway-set-lifecycle', {
    device_id: deviceId,
    next_status: nextStatus,
  });
}

// ---------------------------------------------------------------------------
// Firmware
// ---------------------------------------------------------------------------

/** Promote a firmware release to a new status. Backend validates promotion rules. */
export async function promoteFirmwareRelease(
  releaseId: string,
  nextStatus: string
): Promise<{ release_status: string }> {
  return invokeAuthedFunction<{ release_status: string }>(supabase, 'gateway-promote-firmware', {
    release_id: releaseId,
    next_status: nextStatus,
  });
}

// ---------------------------------------------------------------------------
// Deployments
// ---------------------------------------------------------------------------

export interface CreateDeploymentInput {
  firmware_release_id: string;
  name?: string;
  strategy: 'immediate' | 'staged' | 'maintenance_window';
  staged_config?: {
    stages?: number[];
    auto_advance?: boolean;
    health_check_minutes?: number;
  };
  maintenance_window?: {
    days?: string[];
    start?: string;
    end?: string;
    timezone?: string;
  } | null;
  target_filter?: {
    all?: boolean;
    gateway_ids?: string[];
    firmware_version?: string | null;
    status?: string | null;
  };
  failure_threshold_pct?: number;
}

export async function createDeployment(input: CreateDeploymentInput): Promise<{ deployment_id: string }> {
  return invokeAuthedFunction<{ deployment_id: string }>(supabase, 'gateway-create-deployment', {
    firmware_release_id: input.firmware_release_id,
    name: input.name ?? null,
    strategy: input.strategy,
    staged_config: input.staged_config ?? null,
    maintenance_window: input.maintenance_window ?? null,
    target_filter: input.target_filter ?? { all: true },
    failure_threshold_pct: input.failure_threshold_pct ?? 20,
  });
}

export async function advanceDeployment(deploymentId: string): Promise<{ success: boolean; current_stage_index: number }> {
  return invokeAuthedFunction(supabase, 'gateway-advance-deployment', { deployment_id: deploymentId });
}

export async function pauseDeployment(deploymentId: string): Promise<{ success: boolean; status: string }> {
  return invokeAuthedFunction(supabase, 'gateway-pause-deployment', { deployment_id: deploymentId });
}

export async function resumeDeployment(deploymentId: string): Promise<{ success: boolean; status: string }> {
  return invokeAuthedFunction(supabase, 'gateway-resume-deployment', { deployment_id: deploymentId });
}

export async function cancelDeployment(deploymentId: string): Promise<{ success: boolean; status: string }> {
  return invokeAuthedFunction(supabase, 'gateway-cancel-deployment', { deployment_id: deploymentId });
}

// ---------------------------------------------------------------------------
// Gateway profiles
// ---------------------------------------------------------------------------

export interface GatewayProfilePropertyDefaultsInput {
  grid_mode?: string;
  battery_capacity_kwh?: number;
  battery_reserve_pct?: number;
  notification_rules_template?: string;
}

export interface CreateGatewayProfileInput {
  name: string;
  description?: string | null;
  poll_interval_ms?: number;
  upload_interval_ms?: number;
  heartbeat_interval_ms?: number;
  command_poll_interval_ms?: number;
  writes_enabled?: boolean;
  ota_channel?: 'pilot' | 'stable';
  expected_devices?: { bus_address: number; protocol: string; equipment_type: string; display_name?: string }[];
  default_property_config?: GatewayProfilePropertyDefaultsInput | null;
  auto_commission?: boolean;
  auto_create_property?: boolean;
  is_default?: boolean;
}

export async function createGatewayProfile(input: CreateGatewayProfileInput): Promise<{ profile_id: string }> {
  return invokeAuthedFunction(supabase, 'gateway-profile-create', input);
}

export async function updateGatewayProfile(
  profileId: string,
  input: Partial<CreateGatewayProfileInput>
): Promise<{ success: boolean }> {
  return invokeAuthedFunction(supabase, 'gateway-profile-update', { profile_id: profileId, ...input });
}

export async function deleteGatewayProfile(profileId: string): Promise<{ success: boolean }> {
  return invokeAuthedFunction(supabase, 'gateway-profile-delete', { profile_id: profileId });
}
